import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useTheme } from '../../contexts/ThemeContext';
import BottomSheet from '../../components/shared/BottomSheet';
import PrimaryButton from '../../components/shared/PrimaryButton';
import BacktestSettings from './BacktestSettings';
import {
  DEFAULT_BACKTEST_QTY,
  DEFAULT_MAX_RISK,
  DEFAULT_POSITION_MODE,
  DEFAULT_RISK_PERCENT,
  DEFAULT_RISK_PERCENT_BASIS,
  DEFAULT_SIZE_MODE,
  DEFAULT_STARTING_BALANCE,
  isPositionMode,
  isRiskPercentBasis,
  isSizeMode,
  parsePositiveNumber,
  qtyFromFixed,
} from '../engine/positionMode';

export function snapshotConfig(value = {}) {
  return {
    positionMode: isPositionMode(value.positionMode) ? value.positionMode : DEFAULT_POSITION_MODE,
    sizeMode: isSizeMode(value.sizeMode) ? value.sizeMode : DEFAULT_SIZE_MODE,
    fixedQty: qtyFromFixed(value.fixedQty ?? DEFAULT_BACKTEST_QTY),
    maxRisk: parsePositiveNumber(value.maxRisk, DEFAULT_MAX_RISK),
    startingBalance: parsePositiveNumber(value.startingBalance, DEFAULT_STARTING_BALANCE),
    riskPercent: parsePositiveNumber(value.riskPercent, DEFAULT_RISK_PERCENT),
    riskPercentBasis: isRiskPercentBasis(value.riskPercentBasis)
      ? value.riskPercentBasis
      : DEFAULT_RISK_PERCENT_BASIS,
  };
}

function configsEqual(a, b) {
  return (
    a.positionMode === b.positionMode
    && a.sizeMode === b.sizeMode
    && a.fixedQty === b.fixedQty
    && a.maxRisk === b.maxRisk
    && a.startingBalance === b.startingBalance
    && a.riskPercent === b.riskPercent
    && a.riskPercentBasis === b.riskPercentBasis
  );
}

export default function BacktestConfigModal({
  show,
  onHide,
  isMobile,
  positionMode,
  sizeMode,
  fixedQty,
  maxRisk,
  startingBalance,
  riskPercent,
  riskPercentBasis,
  onSave,
}) {
  const { colors } = useTheme();
  const committed = useMemo(
    () => snapshotConfig({
      positionMode,
      sizeMode,
      fixedQty,
      maxRisk,
      startingBalance,
      riskPercent,
      riskPercentBasis,
    }),
    [positionMode, sizeMode, fixedQty, maxRisk, startingBalance, riskPercent, riskPercentBasis],
  );
  const [draft, setDraft] = useState(committed);

  useEffect(() => {
    if (show) setDraft(committed);
  }, [show, committed]);

  const parsedDraft = snapshotConfig(draft);
  const dirty = !configsEqual(parsedDraft, committed);

  const patch = (partial) => {
    setDraft((prev) => ({ ...prev, ...partial }));
  };

  const handleSave = () => {
    onSave?.(parsedDraft);
    onHide?.();
  };

  const settings = (
    <BacktestSettings
      positionMode={draft.positionMode}
      onPositionMode={(next) => patch({ positionMode: next })}
      sizeMode={draft.sizeMode}
      onSizeMode={(next) => patch({ sizeMode: next })}
      fixedQty={draft.fixedQty}
      onFixedQty={(next) => patch({ fixedQty: next })}
      maxRisk={draft.maxRisk}
      onMaxRisk={(next) => patch({ maxRisk: next })}
      startingBalance={draft.startingBalance}
      onStartingBalance={(next) => patch({ startingBalance: next })}
      riskPercent={draft.riskPercent}
      onRiskPercent={(next) => patch({ riskPercent: next })}
      riskPercentBasis={draft.riskPercentBasis}
      onRiskPercentBasis={(next) => patch({ riskPercentBasis: next })}
    />
  );

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <PrimaryButton
        size="sm"
        variant="compact"
        disabled={!dirty}
        onClick={handleSave}
      >
        Save
      </PrimaryButton>
    </div>
  );

  if (isMobile) {
    return (
      <BottomSheet
        show={show}
        onHide={onHide}
        title="Config"
        testId="backtest-config-modal"
        footer={footer}
      >
        {settings}
      </BottomSheet>
    );
  }

  return (
    <>
      <style>{`.modal-content.backtest-config-modal-content { background-color: ${colors.bgCard}; border: 1px solid ${colors.border}; color: ${colors.text}; }`}</style>
      <Modal
        show={show}
        onHide={onHide}
        centered
        animation={false}
        data-testid="backtest-config-modal"
        contentClassName="backtest-config-modal-content border-0 overflow-hidden"
      >
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{
            backgroundColor: colors.bgSecondary || colors.bgCard,
            borderBottom: `1px solid ${colors.border}`,
            color: colors.text,
          }}
        >
          <Modal.Title style={{ fontSize: 16, fontWeight: 700, color: colors.text }}>
            Config
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: colors.bgCard,
            color: colors.text,
            fontSize: 13,
            lineHeight: 1.45,
          }}
        >
          {settings}
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: colors.bgSecondary || colors.bgCard,
            borderTop: `1px solid ${colors.border}`,
            justifyContent: 'flex-end',
          }}
        >
          {footer}
        </Modal.Footer>
      </Modal>
    </>
  );
}
